import { useLocation } from 'react-router-dom'

import loadable from 'components/utils/lazyImport'
import config from 'constants/config'
import { LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP } from 'agentHub/routes/AgentHubRoutes'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import useMetaSearchMobileV2 from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { CHECKOUT_HEADER_PATHS } from './constants'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { WEDDING_GIFT_REGISTRY_CART_ROUTE } from 'weddings/constants/giftRegistry'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'

const HeaderMain = loadable(() => import('components/App/Header/HeaderMain/HeaderMain'), <div />)
const FlightsSearchResultsHeader = loadable(() => import('components/Flights/FlightsSearch/FlightsSearchResultsHeader/FlightsSearchResultsHeader'), <div />)
const CarsAirportTransferSearchHeader = loadable(() => import('components/Cars/AirportTransfer/CarsAirportTransferSearchHeader'), <div />)
const HeaderCheckout = loadable(() => import('components/App/Header/HeaderCheckout'), <div />)
const AgentHubGuestHeader = loadable(() => import('agentHub/components/AgentHubGuestHeader'), <div />)

const routesWithNoHeader = [
  '/:regionCode/gift-card/:code',
  '/:regionCode/order/:orderId/experience-gift-details',
  '/:regionCode/trip-planner/trip',
  '/:regionCode/trip-planner/public-trip/:id',
  '/:regionCode/trip-planner/generate-trip',
  '/:regionCode/search/map',
  '/:regionCode/search/store/map',
  '/:regionCode/search/homes-and-villas/map',
  '/:regionCode/search/experiences/map',
  '/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher',
  '/:regionCode/live',
  '/:regionCode/societe/credit-card',
  '/:regionCode/corporate-events/:eventId',
  '/:regionCode/corporate-events/:eventId/faq',
  '/:regionCode/corporate-events/:eventId/other-activities',
  '/:regionCode/corporate-events/:eventId/flights',
  '/:regionCode/corporate-events/:eventId/accommodation',
  '/:regionCode/corporate-events/:eventId/things-to-know',
  '/:regionCode/corporate-events/:eventId/register',
  `/:regionCode${LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP}`,
  '/:regionCode/trip-planner/chat-trip-planner/:chatId?',
  '/:regionCode/account/communication-preferences/unsubscribe',
  '/:regionCode/weddings/:slug',
  '/:regionCode/weddings/:slug/edit',
  '/:regionCode/weddings',
]

const weddingGiftRegistryCartHeaderPath = WEDDING_GIFT_REGISTRY_CART_ROUTE

const stickySearchHeaderPaths = [
  '/:regionCode/hotels',
  '/:regionCode/tours',
  '/:regionCode/cruises',
  '/:regionCode/car-hire',
  '/:regionCode/experiences',
  '/:regionCode/flights',
  '/:regionCode/trip-planner',
  '/:regionCode',
]

const flightsSearchResultsPagePaths = [
  '/:regionCode/flights-search-results',
  '/:regionCode/flights-search-results-multi-city',
]
const transferSearchPagePath = '/:regionCode/search/transfer'

const desktopAndLargerOnlyHeaderPaths = [
  '/:regionCode/support',
]

const searchNavRedesignHiddenHeaderPaths = [
  '/:regionCode/search',
]

function HeaderRoutes() {
  const location = useLocation()
  const pathname = location.pathname

  const agentHubAccountSetupComplete = useAppSelector(state => state.agentHub.agentHubAccount.account?.status === 'onboarded')
  const isMobileOrTablet = useIsMobileOrTablet()
  const isMetaSearchMobileV2 = useMetaSearchMobileV2()
  const isFlightsSearchBarRedesignEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsSearchBarRedesign)
  const isAirportTransferSearchRoute = !!matchLocaleAwarePath(pathname, { path: transferSearchPagePath, exact: true })
  const { isAnyVariant: isCarsAirportTransferExperimentEnabled } = useExperiment(
    ChertoExperiments.carsAirportTransfer,
    { canActivate: config.CARS_AIRPORT_TRANSFER_ENABLED && isAirportTransferSearchRoute },
  )

  if (matchLocaleAwarePath(pathname, { path: weddingGiftRegistryCartHeaderPath, exact: true })) {
    return <HeaderMain />
  }

  if (matchLocaleAwarePath(pathname, { path: routesWithNoHeader })) {
    return null
  }

  if (matchLocaleAwarePath(pathname, { path: CHECKOUT_HEADER_PATHS })) {
    return <HeaderCheckout />
  }

  if (config.agentHub.isEnabled && !agentHubAccountSetupComplete) {
    return <AgentHubGuestHeader />
  }

  if (isFlightsSearchBarRedesignEnabled && matchLocaleAwarePath(pathname, { path: flightsSearchResultsPagePaths, exact: true })) {
    return <FlightsSearchResultsHeader />
  }

  if (config.CARS_AIRPORT_TRANSFER_ENABLED && isCarsAirportTransferExperimentEnabled && isAirportTransferSearchRoute) {
    return <CarsAirportTransferSearchHeader />
  }

  if (matchLocaleAwarePath(pathname, { path: stickySearchHeaderPaths, exact: true })) {
    return <HeaderMain stickySearch />
  }

  if (matchLocaleAwarePath(pathname, { path: desktopAndLargerOnlyHeaderPaths, exact: true })) {
    return <CSSBreakpoint min="desktop">
      <HeaderMain />
    </CSSBreakpoint>
  }

  // The mobile/tablet search redesign renders its own in-page header, so the global one is suppressed here.
  if (isMobileOrTablet && matchLocaleAwarePath(pathname, { path: searchNavRedesignHiddenHeaderPaths, exact: true })) {
    return null
  }

  // Meta search mobile V2 hides the global NavBar (logo, drawer, login, search icon).
  if (isMetaSearchMobileV2) {
    return null
  }

  return <HeaderMain />
}

export default HeaderRoutes
